import { zodResolver } from '@hookform/resolvers/zod';
import {
  DevicesDeviceCommand,
  InputsInputBindingActionType as InputBindingActionType,
  getListInputsQueryKey,
  type InputsInput,
  type InputsInputBinding,
  type InputsInputBindingActionType,
  type InputsInputEvent,
  useListInputs,
  useListInputBindings,
  useListInputEvents,
  useCreateInputBinding,
  useUpdateInputBinding,
  useDeleteInputBinding,
  useListScenes,
  useListDevices,
} from '@smarthome/api';
import { ROUTES, useInputStream, type InputStreamEvent } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Plus, Pencil, Trash2, AlertCircle, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  Badge,
  Button,
  IconButton,
  Modal,
  Select,
  TextField,
  TextareaField,
  Toggle,
  cn,
  useToast,
} from '@/components/ui';

const ACTION_TYPE_OPTIONS = [
  { value: InputBindingActionType.InputBindingActionSceneActivate, label: 'Activate scene' },
  { value: InputBindingActionType.InputBindingActionSceneDeactivate, label: 'Deactivate scene' },
  { value: InputBindingActionType.InputBindingActionSceneToggle, label: 'Toggle scene' },
  { value: InputBindingActionType.InputBindingActionDeviceState, label: 'Set device state' },
  { value: InputBindingActionType.InputBindingActionDeviceCommand, label: 'Send device command' },
];

const ACTION_TYPE_LABELS: Record<InputsInputBindingActionType, string> = {
  [InputBindingActionType.InputBindingActionSceneActivate]: 'Scene activation',
  [InputBindingActionType.InputBindingActionSceneDeactivate]: 'Scene deactivation',
  [InputBindingActionType.InputBindingActionSceneToggle]: 'Scene toggle',
  [InputBindingActionType.InputBindingActionDeviceState]: 'Device state',
  [InputBindingActionType.InputBindingActionDeviceCommand]: 'Device command',
};

const ACTION_TYPE_VALUES = Object.values(InputBindingActionType) as InputsInputBindingActionType[];
const DEFAULT_ACTION_TYPE = InputBindingActionType.InputBindingActionSceneActivate;

const bindingSchema = z
  .object({
    name: z.string().max(255, 'Name cannot exceed 255 characters').optional(),
    enabled: z.boolean(),
    payloadFilter: z.string().optional(),
    actionType: z.enum(
      ACTION_TYPE_VALUES as [InputsInputBindingActionType, ...InputsInputBindingActionType[]]
    ),
    sceneId: z.string().optional(),
    durationSeconds: z.string().optional(),
    deviceHumanId: z.string().optional(),
    deviceCommand: z.string().optional(),
    deviceState: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.payloadFilter && data.payloadFilter.trim()) {
      try {
        const parsed = JSON.parse(data.payloadFilter);
        if (!isJsonObject(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Payload filter must be a JSON object',
            path: ['payloadFilter'],
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Payload filter must be valid JSON',
          path: ['payloadFilter'],
        });
      }
    }

    if (isSceneAction(data.actionType)) {
      if (!data.sceneId || !data.sceneId.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scene is required',
          path: ['sceneId'],
        });
      }
      if (data.durationSeconds && data.durationSeconds.trim()) {
        const parsed = Number(data.durationSeconds);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duration must be a positive number',
            path: ['durationSeconds'],
          });
        }
      }
    }

    if (data.actionType === InputBindingActionType.InputBindingActionDeviceState) {
      if (!data.deviceHumanId || !data.deviceHumanId.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Device is required',
          path: ['deviceHumanId'],
        });
      }
      if (!data.deviceState || !data.deviceState.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Device state is required',
          path: ['deviceState'],
        });
      } else {
        try {
          const parsed = JSON.parse(data.deviceState);
          if (!isJsonObject(parsed)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Device state must be a JSON object',
              path: ['deviceState'],
            });
          }
        } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Device state must be valid JSON',
            path: ['deviceState'],
          });
        }
      }
    }

    if (data.actionType === InputBindingActionType.InputBindingActionDeviceCommand) {
      if (!data.deviceHumanId || !data.deviceHumanId.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Device is required',
          path: ['deviceHumanId'],
        });
      }
      if (!data.deviceCommand || !data.deviceCommand.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Command is required',
          path: ['deviceCommand'],
        });
      }
    }
  });

type BindingFormValues = z.infer<typeof bindingSchema>;

const DEFAULT_FORM_VALUES: BindingFormValues = {
  name: '',
  enabled: true,
  payloadFilter: '',
  actionType: InputBindingActionType.InputBindingActionSceneActivate,
  sceneId: '',
  durationSeconds: '',
  deviceHumanId: '',
  deviceCommand: '',
  deviceState: '',
};

const EVENTS_PAGE_SIZE = 50;
const MAX_LIVE_EVENTS = 200;

const DEVICE_COMMAND_OPTIONS = Object.values(DevicesDeviceCommand).map((command) => ({
  value: command,
  label: formatCommandLabel(command),
}));

export function InputsSettingsPage() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedInputId, setSelectedInputId] = useState('');
  const [editingBinding, setEditingBinding] = useState<InputsInputBinding | null>(null);
  const [isBindingModalOpen, setIsBindingModalOpen] = useState(false);
  const [bindingToDelete, setBindingToDelete] = useState<InputsInputBinding | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [eventCursor, setEventCursor] = useState<string | undefined>(undefined);
  const [inputEvents, setInputEvents] = useState<InputsInputEvent[]>([]);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);

  const {
    data: inputsData,
    isLoading: inputsLoading,
    isError: inputsError,
    refetch,
  } = useListInputs();
  const inputs = useMemo(() => inputsData?.inputs ?? [], [inputsData]);

  useEffect(() => {
    if (!selectedInputId && inputs.length > 0) {
      const first = inputs.find((input) => input.id);
      if (first?.id) {
        setSelectedInputId(first.id);
      }
    }
  }, [inputs, selectedInputId]);

  const selectedInput = inputs.find((input) => input.id === selectedInputId) ?? null;

  const {
    data: bindingsData,
    isLoading: bindingsLoading,
    isError: bindingsError,
    refetch: refetchBindings,
  } = useListInputBindings(selectedInputId, {
    query: { enabled: !!selectedInputId },
  });

  const bindings = bindingsData?.bindings ?? [];

  const {
    data: eventsData,
    isLoading: eventsLoading,
    isError: eventsError,
    isFetching: eventsFetching,
    refetch: refetchEvents,
  } = useListInputEvents(
    selectedInputId,
    { limit: EVENTS_PAGE_SIZE, before: eventCursor },
    { query: { enabled: !!selectedInputId } }
  );

  const handleInputStreamEvent = useCallback(
    (event: InputStreamEvent) => {
      if (!event.inputId) {
        return;
      }

      if (event.type === 'input_event_reported' && event.event) {
        const occurredAt = event.event.occurredAt ?? event.timestamp;

        queryClient.setQueryData<{ inputs: InputsInput[] }>(getListInputsQueryKey(), (oldData) => {
          if (!oldData?.inputs) {
            return oldData;
          }

          const updatedInputs = oldData.inputs.map((input) => {
            if (input.id === event.inputId || input.humanId === event.humanId) {
              return {
                ...input,
                lastSeen: occurredAt,
                name: event.name ?? input.name,
              };
            }
            return input;
          });

          return { ...oldData, inputs: updatedInputs };
        });

        if (selectedInputId && event.inputId === selectedInputId) {
          const incoming = event.event as InputsInputEvent;
          setInputEvents((prev) => {
            if (incoming.id && prev.some((item) => item.id === incoming.id)) {
              return prev;
            }
            const next = [incoming, ...prev];
            return next.slice(0, MAX_LIVE_EVENTS);
          });
        }
      } else if (
        event.type === 'input_registered' ||
        event.type === 'input_updated' ||
        event.type === 'input_unregistered'
      ) {
        queryClient.invalidateQueries({ queryKey: getListInputsQueryKey() });
      }
    },
    [queryClient, selectedInputId]
  );

  const { isConnected: isInputStreamConnected } = useInputStream({
    enabled: true,
    onEvent: handleInputStreamEvent,
  });

  const { data: scenesData } = useListScenes();
  const { data: devicesData } = useListDevices();

  const sceneOptions = useMemo(
    () =>
      (scenesData?.scenes ?? [])
        .filter((scene) => scene.id)
        .map((scene) => ({
          value: scene.id ?? '',
          label: scene.name ?? scene.humanId ?? 'Unnamed scene',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [scenesData]
  );

  const deviceOptions = useMemo(
    () =>
      (devicesData?.devices ?? [])
        .filter((device) => device.humanId)
        .map((device) => ({
          value: device.humanId ?? '',
          label: device.name ? `${device.name} (${device.humanId})` : (device.humanId ?? 'Device'),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [devicesData]
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<BindingFormValues>({
    resolver: zodResolver(bindingSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const actionType = watch('actionType');

  useEffect(() => {
    if (!isBindingModalOpen) {
      return;
    }
    reset(toFormValues(editingBinding));
  }, [editingBinding, isBindingModalOpen, reset]);

  useEffect(() => {
    if (!selectedInputId) {
      return;
    }
    setEditingBinding(null);
    setIsBindingModalOpen(false);
  }, [selectedInputId]);

  useEffect(() => {
    setInputEvents([]);
    setEventCursor(undefined);
    setHasMoreEvents(true);
  }, [selectedInputId]);

  useEffect(() => {
    if (!eventsData?.events) {
      if (!eventsLoading && !eventsFetching && !eventCursor) {
        setInputEvents([]);
        setHasMoreEvents(false);
      }
      return;
    }

    const fetched = eventsData.events;
    setInputEvents((prev) => {
      if (!eventCursor) {
        return fetched;
      }
      const existingIds = new Set(prev.map((event) => event.id).filter((id): id is string => !!id));
      const merged = [...prev];
      for (const event of fetched) {
        if (!event.id || !existingIds.has(event.id)) {
          merged.push(event);
        }
      }
      return merged;
    });
    setHasMoreEvents(fetched.length === EVENTS_PAGE_SIZE);
  }, [eventsData, eventsLoading, eventsFetching, eventCursor]);

  const { mutate: createBinding, isPending: isCreating } = useCreateInputBinding({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Binding created', variant: 'success' });
        setIsBindingModalOpen(false);
        setEditingBinding(null);
        refetchBindings();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to create binding', variant: 'error' });
      },
    },
  });

  const { mutate: updateBinding, isPending: isUpdating } = useUpdateInputBinding({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Binding updated', variant: 'success' });
        setIsBindingModalOpen(false);
        setEditingBinding(null);
        refetchBindings();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to update binding', variant: 'error' });
      },
    },
  });

  const { mutate: deleteBinding, isPending: isDeleting } = useDeleteInputBinding({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Binding deleted', variant: 'success' });
        setIsDeleteConfirmOpen(false);
        setBindingToDelete(null);
        refetchBindings();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to delete binding', variant: 'error' });
      },
    },
  });

  const handleOpenCreate = () => {
    if (!selectedInput) {
      addToast({ message: 'Select an input first', variant: 'error' });
      return;
    }
    setEditingBinding(null);
    setIsBindingModalOpen(true);
  };

  const handleOpenEdit = (binding: InputsInputBinding) => {
    setEditingBinding(binding);
    setIsBindingModalOpen(true);
  };

  const handleOpenDelete = (binding: InputsInputBinding) => {
    setBindingToDelete(binding);
    setIsDeleteConfirmOpen(true);
  };

  const handleLoadMoreEvents = () => {
    if (eventsFetching || inputEvents.length === 0) {
      return;
    }
    const oldest = inputEvents[inputEvents.length - 1];
    const before = oldest?.occurredAt ?? oldest?.createdAt;
    if (!before) {
      setHasMoreEvents(false);
      return;
    }
    setEventCursor(before);
  };

  const handleRefreshEvents = () => {
    if (!selectedInputId) {
      return;
    }
    if (eventCursor) {
      setInputEvents([]);
      setHasMoreEvents(true);
      setEventCursor(undefined);
      return;
    }
    refetchEvents();
  };

  const onSubmit = (data: BindingFormValues) => {
    if (!selectedInput?.id) {
      addToast({ message: 'Select an input first', variant: 'error' });
      return;
    }

    const payloadFilter = parseOptionalJsonObject(data.payloadFilter);
    const actionConfig = buildActionConfig(data);

    const request = {
      name: data.name?.trim() ? data.name.trim() : undefined,
      enabled: data.enabled,
      payloadFilter,
      actionType: data.actionType,
      actionConfig,
    };

    if (editingBinding?.id) {
      updateBinding({ id: selectedInput.id, bindingId: editingBinding.id, data: request });
    } else {
      createBinding({ id: selectedInput.id, data: request });
    }
  };

  const handleConfirmDelete = () => {
    if (!selectedInput?.id || !bindingToDelete?.id) {
      addToast({ message: 'Select a binding to delete', variant: 'error' });
      return;
    }
    deleteBinding({ id: selectedInput.id, bindingId: bindingToDelete.id });
  };

  const isSaving = isCreating || isUpdating;
  const lastSeenLabel = selectedInput?.lastSeen
    ? formatDistanceToNow(new Date(selectedInput.lastSeen), { addSuffix: true })
    : 'Never';
  const latestEvent = inputEvents.length > 0 ? inputEvents[0] : null;
  const historyEvents = inputEvents.length > 1 ? inputEvents.slice(1) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={ROUTES.SETTINGS}
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Inputs
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            Configure input bindings for buttons, dials, and sensors
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-white/[0.06]">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Inputs</h2>
                <p className="text-xs text-gray-500 dark:text-zinc-500">
                  {inputs.length} registered
                </p>
              </div>
              <IconButton
                icon={<RefreshCw className="h-4 w-4" />}
                label="Refresh inputs"
                variant="ghost"
                onClick={() => refetch()}
              />
            </div>
            {inputsLoading ? (
              <div className="flex items-center justify-center p-6">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
              </div>
            ) : inputsError ? (
              <div className="flex items-center gap-2 p-4 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                Failed to load inputs
              </div>
            ) : inputs.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-zinc-500">
                No inputs registered yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
                {inputs.map((input) => {
                  const isSelected = selectedInputId === input.id;
                  return (
                    <button
                      key={input.id ?? input.humanId ?? input.name}
                      type="button"
                      onClick={() => input.id && setSelectedInputId(input.id)}
                      className={cn(
                        'flex w-full flex-col gap-1.5 px-4 py-3 text-left transition-colors',
                        isSelected
                          ? 'bg-blue-50 dark:bg-blue-500/10'
                          : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                      )}
                    >
                      <span
                        className={cn(
                          'text-sm font-medium',
                          isSelected
                            ? 'text-blue-900 dark:text-blue-100'
                            : 'text-gray-900 dark:text-zinc-100'
                        )}
                      >
                        {input.name ?? input.humanId ?? 'Unnamed input'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-zinc-500">
                        {input.provider ?? 'Unknown provider'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="border-b border-gray-200 p-4 dark:border-white/[0.06]">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Input details</h2>
            </div>
            {selectedInput ? (
              <div className="space-y-4 p-4 text-sm text-gray-600 dark:text-zinc-400">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="info">{selectedInput.provider ?? 'Unknown provider'}</Badge>
                  {selectedInput.kind && <Badge variant="default">{selectedInput.kind}</Badge>}
                  <Badge variant="default">Last seen {lastSeenLabel}</Badge>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400">Name</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedInput.name ?? selectedInput.humanId ?? 'Unnamed input'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400">Human ID</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedInput.humanId ?? 'Unknown'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400">
                    Payload schema
                  </div>
                  {selectedInput.payloadSchema ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant="default">
                        {selectedInput.payloadSchema.variants?.length
                          ? `${selectedInput.payloadSchema.variants.length} variants`
                          : 'Provided'}
                      </Badge>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-zinc-500">
                      No payload schema provided.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 text-sm text-gray-500 dark:text-zinc-500">
                Select an input to view details and bindings.
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-white/[0.06]">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Event history
                </h2>
                <p className="text-xs text-gray-500 dark:text-zinc-500">
                  {inputEvents.length} loaded
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={isInputStreamConnected ? 'success' : 'default'}>
                  {isInputStreamConnected ? 'Live' : 'Offline'}
                </Badge>
                <IconButton
                  icon={<RefreshCw className="h-4 w-4" />}
                  label="Refresh events"
                  variant="ghost"
                  onClick={handleRefreshEvents}
                  disabled={!selectedInput}
                />
              </div>
            </div>
            {!selectedInput ? (
              <div className="p-4 text-sm text-gray-500 dark:text-zinc-500">
                Select an input to view event history.
              </div>
            ) : eventsLoading ? (
              <div className="flex items-center justify-center p-6">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
              </div>
            ) : eventsError ? (
              <div className="flex items-center gap-2 p-4 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                Failed to load events
              </div>
            ) : inputEvents.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-zinc-500">
                No events received yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
                {latestEvent && (
                  <div className="p-4">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 dark:border-blue-500/20 dark:bg-blue-500/10">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                          Latest event
                        </div>
                        <Badge variant="info">Event</Badge>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                        {formatEventTime(getEventTimestamp(latestEvent))}
                      </div>
                      {formatEventPayload(latestEvent.payload) ? (
                        <pre className="mt-3 max-h-48 overflow-auto rounded-md bg-white/80 p-3 text-xs text-gray-700 dark:bg-white/[0.04] dark:text-zinc-200">
                          {formatEventPayload(latestEvent.payload)}
                        </pre>
                      ) : (
                        <div className="mt-3 text-xs text-gray-500 dark:text-zinc-500">
                          No payload
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
                  {historyEvents.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500 dark:text-zinc-500">
                      No older events yet.
                    </div>
                  ) : (
                    historyEvents.map((event) => {
                      const payload = formatEventPayload(event.payload);
                      return (
                        <div
                          key={
                            event.id ??
                            `${event.inputId ?? 'input'}-${event.occurredAt ?? event.createdAt ?? 'event'}`
                          }
                          className="p-4"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="default">Event</Badge>
                              <span className="text-xs text-gray-500 dark:text-zinc-500">
                                {formatEventTime(getEventTimestamp(event))}
                              </span>
                            </div>
                          </div>
                          {payload ? (
                            <pre className="mt-2 max-h-32 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700 dark:bg-white/[0.04] dark:text-zinc-200">
                              {payload}
                            </pre>
                          ) : (
                            <div className="mt-2 text-xs text-gray-500 dark:text-zinc-500">
                              No payload
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="flex items-center justify-between p-4">
                  {hasMoreEvents ? (
                    <Button
                      variant="ghost"
                      onClick={handleLoadMoreEvents}
                      loading={eventsFetching && !!eventCursor}
                    >
                      Load older events
                    </Button>
                  ) : (
                    <div className="text-xs text-gray-500 dark:text-zinc-500">No more events.</div>
                  )}
                  {eventsFetching && !eventCursor && (
                    <div className="text-xs text-gray-500 dark:text-zinc-500">Refreshing...</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-white/[0.06]">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Bindings</h2>
                <p className="text-xs text-gray-500 dark:text-zinc-500">
                  {bindings.length} configured
                </p>
              </div>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={handleOpenCreate}
                disabled={!selectedInput}
              >
                New binding
              </Button>
            </div>
            {bindingsLoading ? (
              <div className="flex items-center justify-center p-6">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
              </div>
            ) : bindingsError ? (
              <div className="flex items-center gap-2 p-4 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                Failed to load bindings
              </div>
            ) : bindings.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-zinc-500">
                No bindings configured for this input yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
                {bindings.map((binding) => (
                  <div
                    key={binding.id}
                    className="flex flex-wrap items-start justify-between gap-4 p-4"
                  >
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {binding.name ?? getActionLabel(binding.actionType)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-zinc-500">
                        {getActionLabel(binding.actionType)}
                        {binding.payloadFilter ? ' • Filtered' : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={binding.enabled ? 'success' : 'default'}>
                        {binding.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <IconButton
                        icon={<Pencil className="h-4 w-4" />}
                        label="Edit binding"
                        onClick={() => handleOpenEdit(binding)}
                      />
                      <IconButton
                        icon={<Trash2 className="h-4 w-4" />}
                        label="Delete binding"
                        variant="danger"
                        onClick={() => handleOpenDelete(binding)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isBindingModalOpen}
        onClose={() => setIsBindingModalOpen(false)}
        title={editingBinding ? 'Edit binding' : 'New binding'}
        subtitle={
          selectedInput ? `Input: ${selectedInput.name ?? selectedInput.humanId}` : undefined
        }
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setIsBindingModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} loading={isSaving}>
              {editingBinding ? 'Save changes' : 'Create binding'}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <TextField label="Name" placeholder="Optional label" {...register('name')} />

          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <Toggle
                label="Enabled"
                description="Disable to keep the binding without executing actions"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <TextareaField
            label="Payload filter"
            placeholder='{"button":"up"}'
            hint="Optional JSON object to match against incoming payloads"
            error={errors.payloadFilter?.message}
            {...register('payloadFilter')}
          />

          <Controller
            name="actionType"
            control={control}
            render={({ field }) => (
              <Select
                label="Action"
                value={field.value}
                onChange={field.onChange}
                options={ACTION_TYPE_OPTIONS}
                error={errors.actionType?.message}
              />
            )}
          />

          {isSceneAction(actionType) && (
            <div className="space-y-4">
              <Controller
                name="sceneId"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Scene"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={sceneOptions}
                    error={errors.sceneId?.message}
                    disabled={sceneOptions.length === 0}
                  />
                )}
              />
              {sceneOptions.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-zinc-500">No scenes available yet.</p>
              )}
              <TextField
                label="Duration override (seconds)"
                placeholder="Optional"
                type="number"
                error={errors.durationSeconds?.message}
                {...register('durationSeconds')}
              />
            </div>
          )}

          {actionType === InputBindingActionType.InputBindingActionDeviceState && (
            <div className="space-y-4">
              <Controller
                name="deviceHumanId"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Device"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={deviceOptions}
                    error={errors.deviceHumanId?.message}
                    disabled={deviceOptions.length === 0}
                  />
                )}
              />
              {deviceOptions.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  No devices available yet.
                </p>
              )}
              <TextareaField
                label="Device state"
                placeholder='{"switch":{"on":true}}'
                hint="JSON state payload to apply to the device"
                error={errors.deviceState?.message}
                {...register('deviceState')}
              />
            </div>
          )}

          {actionType === InputBindingActionType.InputBindingActionDeviceCommand && (
            <div className="space-y-4">
              <Controller
                name="deviceHumanId"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Device"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={deviceOptions}
                    error={errors.deviceHumanId?.message}
                    disabled={deviceOptions.length === 0}
                  />
                )}
              />
              {deviceOptions.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  No devices available yet.
                </p>
              )}
              <Controller
                name="deviceCommand"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Command"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={DEVICE_COMMAND_OPTIONS}
                    error={errors.deviceCommand?.message}
                  />
                )}
              />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="Delete binding"
        subtitle="This cannot be undone"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Are you sure you want to delete the binding{' '}
          <span className="font-medium text-gray-900 dark:text-white">
            {bindingToDelete?.name ?? getActionLabel(bindingToDelete?.actionType)}
          </span>
          ?
        </p>
      </Modal>
    </div>
  );
}

function isSceneAction(actionType: InputsInputBindingActionType) {
  return (
    actionType === InputBindingActionType.InputBindingActionSceneActivate ||
    actionType === InputBindingActionType.InputBindingActionSceneDeactivate ||
    actionType === InputBindingActionType.InputBindingActionSceneToggle
  );
}

function parseOptionalJsonObject(value?: string) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const parsed = JSON.parse(value);
  if (!isJsonObject(parsed)) {
    throw new Error('JSON value must be an object');
  }
  return parsed as Record<string, unknown>;
}

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toFormValues(binding: InputsInputBinding | null): BindingFormValues {
  if (!binding) {
    return DEFAULT_FORM_VALUES;
  }

  const actionType = binding.actionType ?? DEFAULT_FORM_VALUES.actionType;
  const config = binding.actionConfig ?? {};

  const baseValues: BindingFormValues = {
    ...DEFAULT_FORM_VALUES,
    name: binding.name ?? '',
    enabled: binding.enabled ?? true,
    payloadFilter: formatJson(binding.payloadFilter),
    actionType,
  };

  if (isSceneAction(actionType)) {
    return {
      ...baseValues,
      sceneId: getStringValue(config, 'sceneId'),
      durationSeconds: getNumberString(config, 'durationSeconds'),
    };
  }

  if (actionType === InputBindingActionType.InputBindingActionDeviceState) {
    return {
      ...baseValues,
      deviceHumanId: getStringValue(config, 'deviceHumanId'),
      deviceState: formatJson(getObjectValue(config, 'state')),
    };
  }

  if (actionType === InputBindingActionType.InputBindingActionDeviceCommand) {
    return {
      ...baseValues,
      deviceHumanId: getStringValue(config, 'deviceHumanId'),
      deviceCommand: getStringValue(config, 'command'),
    };
  }

  return baseValues;
}

function buildActionConfig(values: BindingFormValues) {
  if (isSceneAction(values.actionType)) {
    const config: Record<string, unknown> = {
      sceneId: values.sceneId?.trim(),
    };
    if (values.durationSeconds && values.durationSeconds.trim()) {
      config['durationSeconds'] = Number(values.durationSeconds);
    }
    return config;
  }

  if (values.actionType === InputBindingActionType.InputBindingActionDeviceState) {
    return {
      deviceHumanId: values.deviceHumanId?.trim(),
      state: parseOptionalJsonObject(values.deviceState),
    };
  }

  return {
    deviceHumanId: values.deviceHumanId?.trim(),
    command: values.deviceCommand?.trim(),
  };
}

function formatJson(value?: unknown) {
  if (value === null || value === undefined) {
    return '';
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

function getStringValue(value: unknown, key: string) {
  if (!isJsonObject(value)) {
    return '';
  }
  const found = value[key];
  return typeof found === 'string' ? found : '';
}

function getNumberString(value: unknown, key: string) {
  if (!isJsonObject(value)) {
    return '';
  }
  const found = value[key];
  return typeof found === 'number' ? String(found) : '';
}

function getObjectValue(value: unknown, key: string) {
  if (!isJsonObject(value)) {
    return null;
  }
  const found = value[key];
  return isJsonObject(found) ? found : null;
}

function formatCommandLabel(value: string) {
  return value.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
}

function getActionLabel(actionType?: InputsInputBindingActionType) {
  const resolved = actionType ?? DEFAULT_ACTION_TYPE;
  return ACTION_TYPE_LABELS[resolved] ?? 'Action';
}

function getEventTimestamp(event: InputsInputEvent) {
  return event.occurredAt ?? event.createdAt;
}

function formatEventTime(value?: string) {
  if (!value) {
    return 'Unknown time';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown time';
  }
  return `${formatDistanceToNow(date, { addSuffix: true })} - ${date.toLocaleString()}`;
}

function formatEventPayload(payload: InputsInputEvent['payload']) {
  if (payload === undefined) {
    return '';
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}
